/**
 * Holiday calendar for Los Portales bakery analytics.
 * Used for hot zone detection and upcoming holiday alerts.
 *
 * Each holiday has:
 * - name: Spanish display name
 * - keyProduct: Primary product type driven by this holiday
 * - getDateRange(year): Returns { start, end } as YYYY-MM-DD strings
 */

/**
 * Get Easter Sunday for a given year using the Anonymous Gregorian algorithm.
 */
function getEasterDate(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

/**
 * Get the Nth occurrence of a weekday in a given month.
 * @param {number} year
 * @param {number} month - 0-indexed (0=Jan, 5=Jun)
 * @param {number} dayOfWeek - 0=Sun, 1=Mon, ..., 6=Sat
 * @param {number} n - Which occurrence (1=first, 2=second, etc.)
 */
function getNthWeekday(year, month, dayOfWeek, n) {
  const first = new Date(year, month, 1);
  let diff = dayOfWeek - first.getDay();
  if (diff < 0) diff += 7;
  const day = 1 + diff + (n - 1) * 7;
  return new Date(year, month, day);
}

function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const HOLIDAYS = [
  {
    id: 'dia_de_reyes',
    name: 'Dia de Reyes',
    keyProduct: 'Roscas',
    getDateRange: (year) => ({
      start: `${year}-01-01`,
      end: `${year}-01-06`,
    }),
  },
  {
    id: 'candelaria',
    name: 'Dia de la Candelaria',
    keyProduct: 'Tamales',
    getDateRange: (year) => ({
      start: `${year}-02-02`,
      end: `${year}-02-02`,
    }),
  },
  {
    id: 'super_bowl',
    name: 'Super Bowl',
    keyProduct: 'Pasteles',
    // Super Bowl is the 2nd Sunday of February
    getDateRange: (year) => {
      const date = getNthWeekday(year, 1, 0, 2); // 2nd Sunday of Feb
      return {
        start: formatDate(new Date(date.getTime() - 2 * 86400000)), // Fri before
        end: formatDate(date),
      };
    },
  },
  {
    id: 'san_valentin',
    name: 'San Valentin',
    keyProduct: 'Pasteles',
    getDateRange: (year) => ({
      start: `${year}-02-10`,
      end: `${year}-02-14`,
    }),
  },
  {
    id: 'semana_santa',
    name: 'Semana Santa',
    keyProduct: 'Pan dulce',
    getDateRange: (year) => {
      const easter = getEasterDate(year);
      // Holy Week: Palm Sunday (7 days before Easter) through Easter
      const palmSunday = new Date(easter.getTime() - 7 * 86400000);
      return {
        start: formatDate(palmSunday),
        end: formatDate(easter),
      };
    },
  },
  {
    id: 'cinco_de_mayo',
    name: 'Cinco de Mayo',
    keyProduct: 'Pasteles',
    getDateRange: (year) => ({
      start: `${year}-05-03`,
      end: `${year}-05-05`,
    }),
  },
  {
    id: 'dia_de_las_madres',
    name: 'Dia de las Madres',
    keyProduct: 'Pasteles',
    getDateRange: (year) => ({
      start: `${year}-05-08`,
      end: `${year}-05-10`,
    }),
  },
  {
    id: 'dia_del_padre',
    name: 'Dia del Padre',
    keyProduct: 'Pasteles',
    // 3rd Sunday of June
    getDateRange: (year) => {
      const date = getNthWeekday(year, 5, 0, 3); // 3rd Sunday of June
      const start = new Date(date.getTime() - 2 * 86400000); // Fri before
      return {
        start: formatDate(start),
        end: formatDate(date),
      };
    },
  },
  {
    id: 'independence_day_us',
    name: 'Independence Day (US)',
    keyProduct: 'Pasteles',
    getDateRange: (year) => ({
      start: `${year}-07-02`,
      end: `${year}-07-04`,
    }),
  },
  {
    id: 'fiestas_patrias',
    name: 'Fiestas Patrias',
    keyProduct: 'Pasteles',
    getDateRange: (year) => ({
      start: `${year}-09-15`,
      end: `${year}-09-16`,
    }),
  },
  {
    id: 'halloween',
    name: 'Halloween',
    keyProduct: 'Pasteles tematicos',
    getDateRange: (year) => ({
      start: `${year}-10-28`,
      end: `${year}-10-31`,
    }),
  },
  {
    id: 'dia_de_muertos',
    name: 'Dia de los Muertos',
    keyProduct: 'Pan de muerto',
    getDateRange: (year) => ({
      start: `${year}-11-01`,
      end: `${year}-11-02`,
    }),
  },
  {
    id: 'thanksgiving',
    name: 'Thanksgiving',
    keyProduct: 'Pies/pasteles',
    // 4th Thursday of November
    getDateRange: (year) => {
      const date = getNthWeekday(year, 10, 4, 4); // 4th Thursday of Nov
      const start = new Date(date.getTime() - 3 * 86400000); // Mon before
      return {
        start: formatDate(start),
        end: formatDate(date),
      };
    },
  },
  {
    id: 'navidad',
    name: 'Navidad',
    keyProduct: 'Pasteles',
    getDateRange: (year) => ({
      start: `${year}-12-22`,
      end: `${year}-12-25`,
    }),
  },
  {
    id: 'ano_nuevo',
    name: 'Ano Nuevo',
    keyProduct: 'Pasteles',
    getDateRange: (year) => ({
      start: `${year}-12-30`,
      end: `${year}-12-31`,
    }),
  },
];

export { HOLIDAYS, getEasterDate, getNthWeekday, formatDate };
export default HOLIDAYS;
